import * as _ from "lodash-es";
import React, { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import "./PurchaseOrders.scss";
import { Link, useNavigate } from "react-router-dom";
import IGetPurchaseOrdersDTO from "@models/DTO/IGetPurchaseOrdersDTO";
import IOnGetPurchaseOrdersDTO from "@models/DTO/IOnGetPurchaseOrdersDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import IAction from "@models/IAction";
import IBreadcrumbs from "@models/IBreadcrumbs";
import IOwTableCols from "@models/IOwTableCols";
import IPurchase_Order from "@models/IPurchase_Order";
import UIService from "@services/UIService";
import UserService from "@services/UserService";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import PurchaseOrderShipmentsDetailView from "../PurchaseOrderShipmentsDetailView/PurchaseOrderShipmentsDetailView";
import PurchaseOrderSidebar from "../PurchaseOrderSidebar/PurchaseOrderSidebar";
import INotification from "@models/INotification";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import IOnProcessPurchaseOrderDTO from "@models/DTO/IOnProcessPurchaseOrderDTO";
import IProcessPurchaseOrderDTO from "@models/DTO/IProcessPurchaseOrderDTO";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import OwActionBar from "@components/shared/OwActionBar/OwActionBar";
import IVendor_Source from "@models/IVendor_Source";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import { SocketContext } from "@contexts/SocketContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import IGetVendorSourcesDTO from "@models/DTO/IGetVendorSourcesDTO";
import IOnGetVendorSourcesDTO from "@models/DTO/IOnGetVendorSourcesDTO";
import PurchaseOrderStateEnum from "@models/PurchaseOrderStateEnum";
import { OwSelectOptionFn } from "@components/shared/OwSelectOptionFn/OwSelectOptionFn";
import SortOrderEnum from "@models/SortOrderEnum";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { ICheckBoxHash } from "@models/ICheckBoxHas";
import { usePurchaseOrdersCols, PurchaseOrderColsProps } from "@hooks/purchase_orders/usePurchaseOrdersCols";
import PoOrderBy from "@models/PoOrderByEnum";
import IOnQBDCreateDTO from "@models/DTO/IOnQBDCreateDTO";
import IQBDCreateDTO from "@models/DTO/IQBDCreateDTO";
import QBDResourceTypesEnum from "@models/QBDResourceTypesEnum";
import IQBDReconcilePurchaseOrdersDTO from "@models/DTO/IQBDReconcilePurchaseOrdersDTO";
import IQBDOnReconcilePurchaseOrdersDTO from "@models/DTO/IQBDOnReconcilePurchaseOrdersDTO";
import { hasEditAccess, hasReadAccess } from "@services/ScreenOptionsService";
import ViewAccessService from "@services/ViewAccessService";
import ISupplierSettings from "@models/ISupplierSettings";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";

export const PurchaseOrders = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const getCrumbs = (): IBreadcrumbs[] => {
        return [
            {
                title: "dashboard",
                url: "/dashboard",
            },
            {
                title: "purchase_orders",
                url: "/purchase_orders",
            },
        ];
    };

    const poStatusOptions: { state: PurchaseOrderStateEnum; title: string }[] = [
        { state: PurchaseOrderStateEnum.PRELIMINARY, title: "preliminary" },
        { state: PurchaseOrderStateEnum.SUBMITTED, title: "submitted" },
        { state: PurchaseOrderStateEnum.IN_PROGRESS, title: "in_progress" },
        { state: PurchaseOrderStateEnum.CONFIRMED, title: "confirmed" },
        { state: PurchaseOrderStateEnum.RECEIVED, title: "received" },
        { state: PurchaseOrderStateEnum.INCOMING, title: "incoming" },
        { state: PurchaseOrderStateEnum.ACCEPTED, title: "accepted" },
        { state: PurchaseOrderStateEnum.SHIPPED, title: "shipped" },
    ];

    const isEmailedOptions = [
        { value: "true", title: "true" },
        { value: "false", title: "false" },
    ];

    const [purchaseOrders, setPurchaseOrders] = React.useState<IPurchase_Order[]>([]);
    const [totalPurchaseOrders, setTotalPurchaseOrders] = React.useState<number>(0);
    const [crumbs, setCrumbs] = React.useState<IBreadcrumbs[]>(getCrumbs());
    const [columns, setColumns] = React.useState<IOwTableCols[]>([]);
    const [offset, setOffset] = React.useState<number>(0);
    const [limit, setLimit] = React.useState<number>(10);
    const [isDataFetching, setIsDataFetching] = React.useState<boolean>(false);
    const [isInitialData, setIsInitialData] = React.useState<boolean>(true);

    const [qbdActionActive, setQbdActionActive] = React.useState<boolean>(false);

    // Change from single selectedId to a set of selected IDs
    const [selectedPurchaseOrderIds, setSelectedPurchaseOrderIds] = React.useState<Set<number>>(new Set());
    const [selectedPurchaseOrders, setSelectedPurchaseOrders] = React.useState<IPurchase_Order[]>([]);
    const [hasOneChecked, setHasOneChecked] = React.useState<boolean>(false);

    const [name, setName] = React.useState<string>("");
    const [status, setStatus] = useState<PurchaseOrderStateEnum | undefined | null>();
    const [isEmailed, setIsEmailed] = useState<string | undefined | null>();
    const [pullQboVendorsActive, setPullQboVendorsActive] = React.useState<boolean>(false);
    const [confirmDeleteRow, setConfirmDeleteRow] = React.useState<string | null>(null);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = React.useState<IPurchase_Order | null>(null);
    const [vendorSourceOptions, setVendorSourceOptions] = React.useState<IVendor_Source[]>([]);
    const [selectedVendorSource, setSelectedVendorSource] = React.useState<IVendor_Source | null>(null);
    const [resetSelectedVendorSource, setResetSelectedVendorSource] = React.useState<boolean>(false);
    const [resetSearch, setResetSearch] = React.useState<boolean>(false);
    const [order, setOrder] = useState("");
    const [orderBy, setOrderBy] = useState<PoOrderBy>(PoOrderBy.UPDATED_AT);
    const [orderDir, setOrderDir] = useState<SortOrderEnum>(SortOrderEnum.DESC);
    const isReadOnly = useMemo(() => {
        return !hasEditAccess(ScreenOptionsEnum.PURCHASE_ORDERS, userModel, supplierModel, ViewAccessService.PurchaseOrders);
    }, [userModel, supplierModel]);

    const toggleConfirmDelete = (row: IPurchase_Order, index: number) => {
        setConfirmDeleteRow(`row-${index}`);
        setSelectedPurchaseOrder(row);
    };

    // Update the handleSelectPurchaseOrder function to handle multiple selections
    const handleSelectPurchaseOrder = (purchaseOrder: IPurchase_Order) => {
        // Create a new Set from the current selections
        const newSelectedIds = new Set(selectedPurchaseOrderIds);

        // Toggle the selected state of this purchase order
        if (newSelectedIds.has(purchaseOrder.id)) {
            // If already selected, remove it
            newSelectedIds.delete(purchaseOrder.id);

            // Remove from the selected purchase orders array
            setSelectedPurchaseOrders((prev) => prev.filter((po) => po.id !== purchaseOrder.id));
        } else {
            // If not selected, add it
            newSelectedIds.add(purchaseOrder.id);

            // Add to the selected purchase orders array
            setSelectedPurchaseOrders((prev) => [...prev, purchaseOrder]);
        }

        // Update the selected IDs
        setSelectedPurchaseOrderIds(newSelectedIds);

        // Update hasOneChecked flag
        setHasOneChecked(newSelectedIds.size > 0);
    };

    const reconcilePurchaseOrders = () => {
        const payload: IQBDReconcilePurchaseOrdersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBDReconcilePurchaseOrders, payload);

        setQbdActionActive(true);
    };

    const createQbdPurchaseOrders = () => {
        const payload: IQBDCreateDTO = {
            resource: QBDResourceTypesEnum.PURCHASE_ORDER,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            purchaseOrderGuids: selectedPurchaseOrders.map((po) => po.guid),
        };

        socket.emit(EndpointsEnum.QBDCreate, payload);

        setQbdActionActive(true);
    };

    const { getColumns, getActions } = usePurchaseOrdersCols({
        toggleConfirmDelete,
        purchaseOrders,
        orderBy,
        orderDir,
        setOrderBy,
        setOrderDir,
        selectedIds: selectedPurchaseOrderIds,
        onSelectRow: handleSelectPurchaseOrder,
        createQbdPurchaseOrders,
        reconcilePurchaseOrders,
        isReadOnly,
    });

    const [actions, setActions] = React.useState<IAction[]>(getActions());

    const onGetPurchaseOrders = (data: IOnGetPurchaseOrdersDTO) => {
        setPurchaseOrders(data.purchaseOrders);
        setTotalPurchaseOrders(data.totalPurchaseOrders);
        setIsInitialData(false);
        setIsDataFetching(false);

        // Clear selections when new data is loaded
        setSelectedPurchaseOrderIds(new Set());
        setSelectedPurchaseOrders([]);
        setSelectedPurchaseOrder(null);
        setHasOneChecked(false);
    };

    const onProcessPurchaseOrder = (data: IOnProcessPurchaseOrderDTO) => {
        const notification: INotification = {
            message: "successfully_processed_purchase_order",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        setSelectedPurchaseOrder(null);
        setSelectedPurchaseOrderIds(new Set());
        setSelectedPurchaseOrders([]);
        setConfirmDeleteRow(null);
        setHasOneChecked(false);

        search();
    };

    const onGetVendorSources = (data: IOnGetVendorSourcesDTO) => {
        if (data.callMeta?.view === "PurchaseOrders") {
            setVendorSourceOptions(data.vendorSources);
        }
    };

    const onQBDCreate = (data: IOnQBDCreateDTO) => {
        const notification: INotification = {
            message: "successfully_queued_purchase_orders",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setQbdActionActive(false);
    };

    const onQBDReconcilePurchaseOrders = (data: IQBDOnReconcilePurchaseOrdersDTO) => {
        const notification: INotification = {
            message: "successfully_reconciled_purchase_orders",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        setQbdActionActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PurchaseOrderOnGetPurchaseOrders, fn: onGetPurchaseOrders },
        { endpoint: EndpointsEnum.PurchaseOrderOnProcessPurchaseOrder, fn: onProcessPurchaseOrder },
        { endpoint: EndpointsEnum.PurchaseOrderOnGetVendorSources, fn: onGetVendorSources },
        { endpoint: EndpointsEnum.QBDOnCreate, fn: onQBDCreate },
        { endpoint: EndpointsEnum.QBDOnReconcilePurchaseOrders, fn: onQBDReconcilePurchaseOrders },
    ];

    useEffect(() => {
        document.title = `Orderwerks >> Purchase Orders`;

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (resetSearch) {
            setResetSearch(false);
            search();
        }
    }, [resetSearch]);

    useEffect(() => {
        setColumns(getColumns());
        setActions(getActions());
    }, [isDataFetching, orderDir, orderBy, hasOneChecked, selectedPurchaseOrderIds]);

    useEffect(() => {
        if (!isDataFetching) {
            search();
        }
    }, [orderDir, orderBy]);

    useEffect(() => {
        setColumns(getColumns());
    }, [purchaseOrders]);

    useEffect(() => {
        if (!isInitialData) {
            search();
        }
    }, [limit, offset]);

    const getPurchaseOrders = (queryString?: string) => {
        const payload: IGetPurchaseOrdersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit,
            offset,
            queryString,
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetPurchaseOrders, payload);

        setIsDataFetching(true);
    };

    const cancelSidebar = (refreshData?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refreshData) {
            getPurchaseOrders();
        }
    };

    const openPurchaseOrderSidebar = (purchaseOrder?: IPurchase_Order) => {
        const component = <PurchaseOrderSidebar cancelFn={cancelSidebar} purchaseOrder={purchaseOrder} />;

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const getDetailViewComponent = (row: IPurchase_Order) => {
        if (userModel.isVendorSourceUser) {
            return null;
        } else {
            return (
                <PurchaseOrderShipmentsDetailView
                    purchaseOrder={row}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    isReadOnly={isReadOnly}
                    uiContext={uiContext}
                />
            );
        }
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const _name = e.target.name;

        switch (_name) {
            case "name":
                setName(value);
                break;
            case "po_state_id":
                setStatus(Number(value) as PurchaseOrderStateEnum);
                break;
            case "order":
                setOrder(value);
                break;
            case "is_emailed":
                setIsEmailed(value);
                break;
            default:
                break;
        }
    };

    const search = () => {
        const query: string[] = [];
        let queryString = "";

        if (!_.isNil(name) && name.length) {
            query.push(`name=${name}`);
        }

        if (!_.isNil(selectedVendorSource)) {
            query.push(`vendorSourceGuid=${selectedVendorSource.guid}`);
        }

        if (!_.isNil(status)) {
            query.push(`po_state_id=${status}`);
        }

        if (!_.isNil(order) && order.length) {
            query.push(`order=${order}`);
        }

        if (!_.isNil(isEmailed)) {
            query.push(`is_emailed=${isEmailed}`);
        }

        query.push(`order_by=${orderBy}`);
        query.push(`order_dir=${orderDir}`);

        if (query.length) {
            queryString = "?";

            queryString += query.join("&");
        }

        getPurchaseOrders(queryString);
    };

    const reset = () => {
        setOffset(0);
        setLimit(25);
        setName("");
        setOrder("");
        setStatus(null);
        setResetSelectedVendorSource(true);
        setOrderBy(PoOrderBy.UPDATED_AT);
        setOrderDir(SortOrderEnum.DESC);
        setIsEmailed(null);
        setResetSearch(true);
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter" || e.keyCode === 13) {
            search();
        }
    };

    const cancelDelete = () => {
        setConfirmDeleteRow(null);
    };

    const commitDelete = () => {
        if (selectedPurchaseOrder) {
            const purchaseOrder = selectedPurchaseOrder;

            const payload: IProcessPurchaseOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                purchaseOrder: purchaseOrder,
                deleteFlag: true,
            };

            socket.emit(EndpointsEnum.PurchaseOrderProcessPurchaseOrder, payload);
        }
    };

    const lookupVendorSource = (vendorSourceName: string) => {
        const payload: IGetVendorSourcesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: `name=${vendorSourceName}`,
            callMeta: {
                view: "PurchaseOrders",
            },
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetVendorSources, payload);
    };

    const resetSelectedVendorSourceFn = () => {
        setSelectedVendorSource(null);
        setResetSelectedVendorSource(false);
    };

    let cardClass = "dark-card text-white bg-secondary";

    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    // Update the checkAll function to handle multiple selections
    const checkAll = (isChecked: boolean) => {
        if (isChecked) {
            // Select all purchase orders
            const allIds = new Set(purchaseOrders.map((po) => po.id));
            setSelectedPurchaseOrderIds(allIds);
            setSelectedPurchaseOrders([...purchaseOrders]);
            setHasOneChecked(purchaseOrders.length > 0);
        } else {
            // Uncheck all
            setSelectedPurchaseOrderIds(new Set());
            setSelectedPurchaseOrders([]);
            setSelectedPurchaseOrder(null);
            setHasOneChecked(false);
        }
    };

    return (
        <div className="PurchaseOrders position-relative">
            <div className="row mt-2">
                <div className={"col-12 align-items-center"}>
                    <div className={"d-flex w-100 align-items-center"}>
                        <OwTitle title={"purchase_orders"} leftIconName={"fad fa-th-list"} />
                        {userModel.isVendorSourceUser || isReadOnly ? null : (
                            <div>
                                <OwButton
                                    className={"ms-2 btn-warning"}
                                    title={"create"}
                                    leftIconClass={"fas fa-plus"}
                                    fn={() => {
                                        openPurchaseOrderSidebar();
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={"mt-2 mb-2"}>
                <div className={"w-100"}>
                    <SearchSection>
                        <div className={"row align-items-end"}>
                            <div className={"col-12 col-md-3 col-lg-2 mb-2 mb-md-0"}>
                                <OwLabel title={"name"} />
                                <OwInputWrapper name={"name"} value={name} fn={handleChange} onKeyDownFn={enterSearch} />
                            </div>
                            <div className={"col-12 col-md-3 col-lg-2 mb-2 mb-md-0"}>
                                <OwLabel title={"vendor_source"} />
                                <TypeaheadFn
                                    lookupFn={lookupVendorSource}
                                    availableOptions={vendorSourceOptions}
                                    selectedOptionFn={(option) => {
                                        setSelectedVendorSource(option);
                                    }}
                                    selectedOption={selectedVendorSource}
                                    valueProp={"guid"}
                                    titleProp={"name"}
                                    resetTypeahead={resetSelectedVendorSource}
                                    resetTypeaheadCallbackFn={resetSelectedVendorSourceFn}
                                />
                            </div>
                            <div className={"col-12 col-md-3 col-lg-2 mb-2 mb-md-0"}>
                                <OwLabel title={"order"} />
                                <OwInputWrapper name={"order"} fn={handleChange} value={order} />
                            </div>
                            <div className={"col-12 col-md-3 col-lg-2 mb-2 mb-md-0"}>
                                <OwLabel title={"state"} />
                                <OwSelectOptionFn
                                    name={"po_state_id"}
                                    titleProp={"title"}
                                    valueProp={"state"}
                                    selectedOption={status}
                                    hasSelectedOptionProp={false}
                                    options={poStatusOptions}
                                    fn={handleChange}
                                    translateTitle={true}
                                />
                            </div>
                            <div className={"col-12 col-md-2 col-lg-1 mb-2 mb-md-0"}>
                                <OwLabel title={"emailed"} />
                                <OwSelectOptionFn
                                    name={"is_emailed"}
                                    titleProp={"title"}
                                    valueProp={"value"}
                                    selectedOption={isEmailed}
                                    hasSelectedOptionProp={false}
                                    options={isEmailedOptions}
                                    fn={handleChange}
                                    translateTitle={true}
                                />
                            </div>
                            <div className={"col-12 col-md-3"}>
                                <OwButton title={"search"} fn={search} />
                                <OwButton title={"reset"} fn={reset} className={"btn-danger ms-2"} />
                            </div>
                        </div>
                    </SearchSection>
                </div>
            </div>

            <div className={""}>
                <OwActionBar toggleSmallTable={() => {}} colToggledFn={() => {}} columns={{}} actions={actions} />
            </div>

            <div className="row position-relative">
                <div className="col-12 table-responsive" id={"po-table-container"}>
                    <ConfirmDeleteOverlayFn
                        targetElem={confirmDeleteRow}
                        handleConfirm={commitDelete}
                        handleCancel={cancelDelete}
                        nestedParentElem={"po-table"}
                        leftAdjustment={-12}
                        skipMainView={true}
                    />

                    <OwTable
                        id={"po-table"}
                        columns={columns}
                        rowDetailViewFn={() => {
                            return true;
                        }}
                        detailViewComponent={getDetailViewComponent}
                        dataSet={purchaseOrders}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                        tableConfig={{ tableStriped: true, hasDetailView: true }}
                        checkAllFn={checkAll}
                        currentSortCol={orderBy}
                    />
                </div>
            </div>

            <div className={"row"}>
                <div className={"col-12"}>
                    <PaginationBar
                        offset={offset}
                        limit={limit}
                        totalRecords={totalPurchaseOrders}
                        onPaginationChanged={paginationChanged}
                    />
                </div>
            </div>
        </div>
    );
};
